import { useState, useEffect, useMemo } from 'react';
import { getProjets, createProjet, updateProjet, deleteProjet, getClients } from '../services/airtable';
import { useToast } from '../contexts/ToastContext';

const EMPTY_FORM = { nom: '', statut: 'Actif', client_id: '' };

export function useProjetsManagement() {
  const { showToast } = useToast();

  const [projets, setProjets]   = useState([]);
  const [clients, setClients]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [showModal, setShowModal]       = useState(false);
  const [modalMode, setModalMode]       = useState('create');
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData]         = useState(EMPTY_FORM);
  const [submitting, setSubmitting]     = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete]       = useState(null);
  const [deleting, setDeleting]               = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const [rawProjets, rawClients] = await Promise.all([getProjets(), getClients()]);

      setProjets((rawProjets || []).map(p => ({
        id:        p.id,
        nom:       p.fields?.['Nom du projet'] || p.fields?.Nom || '',
        statut:    p.fields?.Statut || 'Actif',
        client_id: Array.isArray(p.fields?.['Clients finaux'])
          ? (p.fields['Clients finaux'][0] || null)
          : (p.fields?.['Clients finaux'] || null),
      })));

      setClients((rawClients || []).map(c => ({
        id:  c.id,
        nom: c.fields?.Nom || '',
      })));

      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredProjets = useMemo(() => {
    if (!searchTerm) return projets;
    const q = searchTerm.toLowerCase();
    return projets.filter(p => p.nom.toLowerCase().includes(q));
  }, [projets, searchTerm]);

  const getClientNom = (id) => {
    if (!id) return '-';
    return clients.find(c => c.id === id)?.nom || '-';
  };

  const handleOpenModal = (mode, item = null) => {
    setModalMode(mode);
    setSelectedItem(item);
    setFormData(mode === 'edit' && item
      ? { nom: item.nom, statut: item.statut, client_id: item.client_id || '' }
      : EMPTY_FORM
    );
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedItem(null);
    setFormData(EMPTY_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (modalMode === 'create') {
        await createProjet(formData);
        showToast('Projet créé avec succès', 'success');
      } else {
        await updateProjet(selectedItem.id, formData);
        showToast('Projet modifié avec succès', 'success');
      }
      await load();
      handleCloseModal();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      await deleteProjet(itemToDelete.id);
      showToast('Projet supprimé avec succès', 'success');
      await load();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
      setItemToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  return {
    projets, filteredProjets, clients, loading, error,
    searchTerm, setSearchTerm,
    showModal, modalMode, formData, setFormData, submitting,
    handleOpenModal, handleCloseModal, handleSubmit,
    showDeleteModal, itemToDelete, deleting,
    handleDeleteClick, handleDeleteConfirm, handleDeleteCancel,
    getClientNom,
  };
}
